/**
 * Menu Resolução do participante no modo `prd` (docs/menuresolucao-integracao.md).
 *
 * O servidor é a fonte da verdade e os eventos são só aviso (§1): a tela do jogador pergunta por
 * `obterMenuResolucao()` e recebe sempre o mesmo "painel" que a cena usa em `dev`, montado aqui a
 * partir de `GET /estado`, `/conteudo` e `/resultado`. Cada evento `menuresolucao_*` só força uma
 * nova leitura do estado.
 *
 * Rotas reais do `server-backend`:
 * - `GET  /menuresolucao/abertura`                    — títulos dos Pixels;
 * - `GET  /menuresolucao/estado`                      — janela de cada Pixel, rodada, surpresa do grupo;
 * - `GET  /menuresolucao/pixels/{n}/conteudo`         — só com a janela do Pixel aberta;
 * - `GET  /menuresolucao/pixels/{n}/alternativas`     — letra e texto das opções de um Pixel já aberto, para a tela
 *                                                       "aguarde o resultado" de quem não baixou o conteúdo a tempo;
 * - `POST /menuresolucao/pixels/{n}/resposta`         — um sync por participante, Pixel e rodada;
 * - `GET  /menuresolucao/pixels/{n}/resultado`        — só depois de o facilitador liberar;
 * - `GET  /menuresolucao/minha-pontuacao`             — tela "Resolução completa".
 *
 * O carimbo do tablet decide os pontos (§6): o relógio local é corrigido com `agora_servidor`, o
 * cronômetro conta de `aberta_em` e o `concluido_em` do sync sai do mesmo relógio corrigido. A chave
 * de idempotência é uma por Pixel e por rodada e fica guardada com o corpo até o servidor confirmar.
 */
import {
    carimboDeConclusao, chaveIdempotencia, deslocamentoDoRelogio, montarSync, paraCatalogoMR, paraResultadoMR,
    PIXEL_TRES, pixelEmFoco, reagirAoResultadoDoSync, respostasDoPixel3, tempoRestanteSegundos, tituloDoPixel
} from '../game/services/menuResolucao.js';

const INTERVALO_DO_ESTADO_MS = 4000;
const INTERVALO_DO_ENVIO_MS = 8000;
const PAUSA_APOS_FALHA_MS = 2000;

const CHAVE_ENVIOS = (participanteId) => `fiat-arcade:menuresolucao:envios:v1:${participanteId}`;
const CHAVE_CONTEUDO = (participanteId) => `fiat-arcade:menuresolucao:conteudo:v1:${participanteId}`;

const PAINEL_SEM_PIXEL = Object.freeze({
    pixel: null, rodada: null, janela: 'nao_aberta', tempoRestante: null, surpresaLiberada: {}, resultados: {}
});

/**
 * @param {object} dependencias
 * @param {{ requisitar: Function, enviarGzip: Function }} dependencias.api
 * @param {{ on: Function }} dependencias.rede
 * @param {() => ({ participanteId?: string, grupoId?: string }|null)} dependencias.obterSessao
 */
export function criarMenuResolucaoApi ({
    api, rede, obterSessao,
    armazenamento = globalThis.localStorage,
    agora = () => Date.now(),
    uuid = () => globalThis.crypto.randomUUID(),
    repetir = (fn, ms) => setInterval(fn, ms), parar = (id) => clearInterval(id),
    intervaloDoEstadoMs = INTERVALO_DO_ESTADO_MS, intervaloDoEnvioMs = INTERVALO_DO_ENVIO_MS
}) {
    // Relógio do servidor − relógio local, no instante da última resposta (§6.2).
    let deslocamentoMs = 0;
    let estado = null;
    let estadoLidoEm = 0;
    let lendoEstado = null;
    let abertura = null;
    // Conteúdo e resultado por `pixel:rodada`. Só a rodada vigente de cada Pixel vale (§6.4).
    const conteudos = new Map();
    const resultados = new Map();
    const alternativas = new Map();
    const tentarApos = new Map();
    let envios = {};
    let relogioDoEnvio = null;
    let drenando = false;

    const participanteId = () => obterSessao()?.participanteId ?? null;
    const lerJson = (chave, padrao) => {
        try { return JSON.parse(armazenamento?.getItem(chave) ?? 'null') ?? padrao; } catch { return padrao; }
    };
    const gravarJson = (chave, valor) => {
        try { armazenamento?.setItem(chave, JSON.stringify(valor)); } catch { /* sem storage */ }
    };
    const guardarConteudos = () => {
        const id = participanteId();
        if (id) gravarJson(CHAVE_CONTEUDO(id), Object.fromEntries(conteudos));
    };
    const guardarEnvios = () => {
        const id = participanteId();
        if (id) gravarJson(CHAVE_ENVIOS(id), envios);
    };
    const sincronizarRelogio = (agoraServidor) => {
        if (agoraServidor) deslocamentoMs = deslocamentoDoRelogio(agoraServidor, agora());
    };
    const chaveDe = (pixel, rodada) => `${pixel}:${rodada}`;
    const esquecerPixel = (pixel) => {
        for (const mapa of [conteudos, resultados, alternativas]) {
            for (const chave of [...mapa.keys()]) if (chave.startsWith(`${pixel}:`)) mapa.delete(chave);
        }
        guardarConteudos();
    };
    /** Nunca martela o servidor: depois de uma falha a mesma leitura só se repete após uma pausa. */
    const podeTentar = (chave) => agora() >= (tentarApos.get(chave) ?? 0);
    const falhou = (chave) => tentarApos.set(chave, agora() + PAUSA_APOS_FALHA_MS);

    // ------------------------------------------------------------------- leitura

    const lerAbertura = async () => {
        if (abertura || !podeTentar('abertura')) return;
        try {
            abertura = await api.requisitar('GET', '/menuresolucao/abertura');
        } catch (falha) {
            falhou('abertura');
            console.warn('[MenuResolucao] títulos indisponíveis.', falha?.codigo ?? falha?.message);
        }
    };

    const tituloDe = (pixel) => tituloDoPixel((abertura?.pixels ?? []).find((p) => p.pixel === pixel)?.titulo, pixel);

    const baixarConteudo = async (foco) => {
        const chave = chaveDe(foco.pixel, foco.rodada);
        if (conteudos.has(chave) || !podeTentar(`conteudo:${chave}`)) return;
        try {
            const conteudo = await api.requisitar('GET', `/menuresolucao/pixels/${foco.pixel}/conteudo`);
            sincronizarRelogio(conteudo.agora_servidor);
            // Reinício no meio do caminho: o conteúdo é de outra rodada e não serve a esta.
            if (conteudo.rodada !== foco.rodada) return;
            esquecerPixel(foco.pixel);
            conteudos.set(chave, conteudo);
            guardarConteudos();
        } catch (falha) {
            falhou(`conteudo:${chave}`);
            console.warn('[MenuResolucao] conteúdo do Pixel indisponível.', falha?.codigo ?? falha?.message);
        }
    };

    const lerResultado = async (foco) => {
        const chave = chaveDe(foco.pixel, foco.rodada);
        if (resultados.has(chave) || !podeTentar(`resultado:${chave}`)) return;
        try {
            const resposta = await api.requisitar('GET', `/menuresolucao/pixels/${foco.pixel}/resultado`);
            resultados.set(chave, paraResultadoMR(resposta, obterSessao()?.grupoId ?? null));
        } catch (falha) {
            falhou(`resultado:${chave}`);
            console.warn('[MenuResolucao] resultado indisponível.', falha?.codigo ?? falha?.message);
        }
    };

    /** Janela fechada e sem o conteúdo guardado: só as alternativas, para as opções bloqueadas aparecerem. */
    const lerAlternativas = async (foco) => {
        const chave = chaveDe(foco.pixel, foco.rodada);
        if (alternativas.has(chave) || conteudos.has(chave) || !podeTentar(`alternativas:${chave}`)) return;
        try {
            const resposta = await api.requisitar('GET', `/menuresolucao/pixels/${foco.pixel}/alternativas`);
            if (resposta.rodada === foco.rodada) alternativas.set(chave, resposta.opcoes ?? []);
        } catch (falha) {
            falhou(`alternativas:${chave}`);
            console.warn('[MenuResolucao] alternativas indisponíveis.', falha?.codigo ?? falha?.message);
        }
    };

    /** O que depende do Pixel em foco: conteúdo com a janela aberta, resultado depois de liberado. */
    const complementar = async () => {
        const foco = pixelEmFoco(estado);
        if (!foco || estado?.fase !== 'aberta') return;
        if (foco.janela === 'aberta') {
            await lerAbertura();
            await baixarConteudo(foco);
        } else if (foco.janela === 'encerrada') {
            // Quem recarregou com a janela já fechada ainda tem o conteúdo guardado.
            await lerAbertura();
            if (foco.pixel !== PIXEL_TRES) await lerAlternativas(foco);
            if (foco.resultado_liberado) await lerResultado(foco);
        }
    };

    const atualizarEstado = () => {
        if (lendoEstado) return lendoEstado;
        lendoEstado = (async () => {
            try {
                const novo = await api.requisitar('GET', '/menuresolucao/estado');
                sincronizarRelogio(novo.agora_servidor);
                estado = novo;
                estadoLidoEm = agora();
                // Pixel reiniciado: o conteúdo e o resultado da rodada antiga saem (§6.4).
                for (const p of novo.pixels ?? []) {
                    for (const mapa of [conteudos, resultados, alternativas]) {
                        for (const chave of [...mapa.keys()]) {
                            if (chave.startsWith(`${p.pixel}:`) && chave !== chaveDe(p.pixel, p.rodada)) mapa.delete(chave);
                        }
                    }
                }
                await complementar();
            } catch (falha) {
                console.warn('[MenuResolucao] estado indisponível.', falha?.codigo ?? falha?.message);
            } finally {
                lendoEstado = null;
            }
        })();
        return lendoEstado;
    };

    const catalogoDe = (foco) => {
        const conteudo = conteudos.get(chaveDe(foco.pixel, foco.rodada));
        const titulo = tituloDe(foco.pixel);
        if (conteudo) return paraCatalogoMR({ pixel: foco.pixel, titulo, conteudo });
        // Janela fechada e sem o conteúdo (o servidor não o serve mais): só a moldura da tela.
        if (foco.janela === 'encerrada') {
            if (foco.pixel === PIXEL_TRES) return { titulo, surpresa: true, afirmacoes: [] };
            const opcoes = alternativas.get(chaveDe(foco.pixel, foco.rodada)) ?? [];
            return { titulo, enunciado: '', quantidade_selecao: 1, opcoes };
        }
        return null;
    };

    const montarPainel = () => {
        if (!estado || estado.fase === 'nao_aberta') return null;
        if (estado.fase === 'encerrada') {
            return { ...PAINEL_SEM_PIXEL, moduloEncerrado: true, moduloFinalizado: Boolean(estado.modulo_finalizado) };
        }
        const foco = pixelEmFoco(estado);
        if (!foco) return { ...PAINEL_SEM_PIXEL, moduloEncerrado: false };
        const grupoId = obterSessao()?.grupoId ?? null;
        const resultado = resultados.get(chaveDe(foco.pixel, foco.rodada));
        return {
            pixel: foco.pixel,
            rodada: foco.rodada,
            janela: foco.janela,
            // Pausada, o tempo fica onde parou: conta até o instante da pausa, não até agora.
            tempoRestante: foco.janela !== 'aberta' ? null
                : foco.pausada_em
                    ? Math.max(0, Math.ceil((Date.parse(foco.resposta_ate) - Date.parse(foco.pausada_em)) / 1000))
                    : tempoRestanteSegundos({ respostaAte: foco.resposta_ate, relogioLocalMs: agora(), deslocamentoMs }),
            pausada: foco.janela === 'aberta' && Boolean(foco.pausada),
            moduloEncerrado: false,
            surpresaLiberada: grupoId ? { [grupoId]: Boolean(estado.surpresa?.liberada) } : {},
            resultados: resultado ? { [foco.pixel]: resultado } : {},
            catalogo: catalogoDe(foco)
        };
    };

    // -------------------------------------------------------------------- envio

    const pendentes = () => Object.values(envios).filter((envio) => envio.estado === 'pendente');

    const drenar = async () => {
        if (drenando) return;
        drenando = true;
        try {
            for (const envio of pendentes()) {
                try {
                    await api.enviarGzip(`/menuresolucao/pixels/${envio.pixel}/resposta`, envio.corpo, { 'Idempotency-Key': envio.chave });
                    envio.estado = 'enviado';
                } catch (falha) {
                    const acao = reagirAoResultadoDoSync({ status: falha?.status ?? 0, codigo: falha?.codigo ?? null });
                    if (acao === 'remover') {
                        envio.estado = 'enviado';
                    } else if (acao === 'nova_chave') {
                        envio.chave = chaveIdempotencia({ pixel: envio.pixel, rodada: envio.rodada, uuid: uuid() });
                    } else if (acao === 'erro_permanente') {
                        // Sem loop (§7.7): fica registrado e a cena segue como "enviado".
                        envio.estado = 'erro';
                        console.error(`[MenuResolucao] envio do Pixel ${envio.pixel} recusado (${falha?.codigo ?? falha?.status}).`);
                    }
                    // `manter` e `reautenticar`: a mesma chave, na próxima volta.
                }
                guardarEnvios();
            }
        } finally {
            drenando = false;
            if (pendentes().length === 0 && relogioDoEnvio) {
                parar(relogioDoEnvio);
                relogioDoEnvio = null;
            }
        }
    };

    const agendarEnvio = () => {
        if (pendentes().length === 0) return;
        if (!relogioDoEnvio) relogioDoEnvio = repetir(drenar, intervaloDoEnvioMs);
        return drenar();
    };

    /** Um envio por Pixel e por rodada: pedir de novo não cria outro (o servidor recusaria, §7.1). */
    const enfileirar = ({ pixel, rodada, respostas }) => {
        const chave = chaveDe(pixel, rodada);
        if (envios[chave]) return envios[chave];
        envios[chave] = {
            pixel, rodada, estado: 'pendente',
            chave: chaveIdempotencia({ pixel, rodada, uuid: uuid() }),
            corpo: montarSync({ rodada, concluidoEm: carimboDeConclusao(agora(), deslocamentoMs), respostas })
        };
        guardarEnvios();
        return envios[chave];
    };

    const conteudoDe = (pixel, rodada) => conteudos.get(chaveDe(pixel, rodada)) ?? null;

    // ------------------------------------------------------------------- eventos

    // Os ouvintes só entram quando o módulo é usado (a cena lê o painel ou há envio pendente): as outras
    // fases não pagam por eles, e quem conta ouvintes da rede (Versus) não vê um a mais.
    let cancelarMensagens = null;
    let cancelarPronto = null;
    const ouvir = () => {
        if (!rede?.on || cancelarMensagens) return;
        cancelarMensagens = rede.on('mensagem', (mensagem) => {
            switch (mensagem?.type) {
                // O reinício traz a rodada nova; ao reler o estado, o conteúdo e o resultado da antiga saem (§6.4).
                case 'menuresolucao_pixel_reiniciado':
                case 'menuresolucao_janela_aberta':
                case 'menuresolucao_janela_encerrada':
                case 'menuresolucao_resultado_liberado':
                case 'menuresolucao_surpresa_liberada':
                case 'menuresolucao_pixel_pausado':
                case 'menuresolucao_pixel_retomado':
                case 'menuresolucao_modulo_finalizado':
                    sincronizarRelogio(mensagem.agora_servidor);
                    atualizarEstado();
                    break;
                default:
            }
        });
        // Reconectou: os eventos de Pixel não são reenviados, então o estado é relido (§11) e o que
        // ficou pendente na outbox volta a sair.
        cancelarPronto = rede.on('pronto', () => {
            atualizarEstado();
            agendarEnvio();
        });
    };
    const deixarDeOuvir = () => {
        cancelarMensagens?.();
        cancelarPronto?.();
        cancelarMensagens = null;
        cancelarPronto = null;
    };

    return {
        /**
         * O módulo está em andamento para a turma? O evento de abertura só chega a quem estava conectado:
         * quem entra (ou reconecta) depois lê o estado para descobrir que a fase já está aberta.
         */
        async faseEmAndamento () {
            try {
                const lido = await api.requisitar('GET', '/menuresolucao/estado');
                sincronizarRelogio(lido?.agora_servidor);
                return lido?.fase === 'aberta';
            } catch (falha) {
                console.warn('[MenuResolucao] estado da fase indisponível.', falha?.codigo ?? falha?.message);
                return false;
            }
        },

        /** O painel da cena; sem leitura anterior espera a primeira, depois responde na hora e relê ao fundo. */
        async obterMenuResolucao () {
            ouvir();
            if (!estado) await atualizarEstado();
            else if (agora() - estadoLidoEm >= intervaloDoEstadoMs) atualizarEstado();
            const painel = montarPainel();
            // Janela aberta e conteúdo ainda por baixar (falha anterior): tenta de novo sem esperar o ciclo.
            if (painel?.janela === 'aberta' && !painel.catalogo) atualizarEstado();
            return painel;
        },

        /** Pixels 1, 2, 4 e Extra: `opcoes` são os ids das alternativas marcadas. */
        registrarRespostaPixel ({ pixel, rodada, opcoes = [] }) {
            ouvir();
            const pergunta = conteudoDe(pixel, rodada)?.perguntas?.[0];
            if (!pergunta) {
                console.error(`[MenuResolucao] sem conteúdo do Pixel ${pixel} (rodada ${rodada}); resposta não enviada.`);
                return Promise.resolve();
            }
            enfileirar({ pixel, rodada, respostas: opcoes.map((opcaoId) => ({ pergunta_id: pergunta.id, opcao_id: opcaoId })) });
            return agendarEnvio();
        },

        /** Pixel 3: `respostas` é `'controlo' | 'nao_controlo'` por situação, na ordem exibida. */
        registrarRespostasPixel3 ({ pixel = PIXEL_TRES, rodada, respostas = [] }) {
            ouvir();
            const conteudo = conteudoDe(pixel, rodada);
            if (!conteudo) {
                console.error(`[MenuResolucao] sem conteúdo do Pixel ${pixel} (rodada ${rodada}); resposta não enviada.`);
                return Promise.resolve();
            }
            enfileirar({ pixel, rodada, respostas: respostasDoPixel3(conteudo.perguntas, respostas) });
            return agendarEnvio();
        },

        /** Este tablet já mandou (ou está mandando) o Pixel nesta rodada: a cena não pergunta de novo. */
        respostaEnviada ({ pixel, rodada }) {
            return Boolean(envios[chaveDe(pixel, rodada)]);
        },

        /** `GET /minha-pontuacao`: a nota do Pixel 3 e os pontos do grupo; sem resposta, `null`. */
        async obterMinhaPontuacao () {
            try {
                return await api.requisitar('GET', '/menuresolucao/minha-pontuacao');
            } catch (falha) {
                console.warn('[MenuResolucao] pontuação indisponível.', falha?.codigo ?? falha?.message);
                return null;
            }
        },

        /** Chamado quando o dono do tablet muda: devolve o que ficou guardado dele e retoma o que estava pendente. */
        iniciarSessao () {
            const id = participanteId();
            estado = null;
            abertura = null;
            conteudos.clear();
            resultados.clear();
            alternativas.clear();
            tentarApos.clear();
            envios = id ? lerJson(CHAVE_ENVIOS(id), {}) : {};
            for (const [chave, conteudo] of Object.entries(id ? lerJson(CHAVE_CONTEUDO(id), {}) : {})) conteudos.set(chave, conteudo);
            if (pendentes().length > 0) ouvir();
            return agendarEnvio();
        },

        encerrar () {
            deixarDeOuvir();
            if (relogioDoEnvio) parar(relogioDoEnvio);
            relogioDoEnvio = null;
        }
    };
}
